/* eslint-disable consistent-return */
import { Mongo } from 'meteor/mongo';
import SimpleSchema from 'simpl-schema';
import { includes, map, pick, values } from 'lodash';

import requiredIf from '../../modules/schema-conditional-required';

/**
 * A connector object for iTasker.
 *
 * @typedef {object} Connector
 * @property {string} _id - The connector id.
 * @property {string} createdAt - The date when it was created.
 * @property {string} deleted - Indicates whether it is in the trash or not.
 * @property {string} isOutput - Flag to check if the connector is an output one.
 * @property {string} name - The name for the connector.
 * @property {string} owner - The owner id.
 * @property {string} subtype - The subtype for the connector. One of [Vector,Thematic,Raster,PointCloud,Generic].
 * @property {string} type - The type of connector you are working with. One of [FTP,PostgreSQL,HTTP].
 * @property {string} updatedAt - The date when it was last updated.
 * @property {string} [databaseName] - The database name for the connector (required in [PostgreSQL]).
 * @property {string} [domain] - The domain for the connector.
 * @property {string} [filePath] - The URL of the file.
 * @property {string} [fileType] - The file types allowed. One of [SHP,KML,GEOJSON].
 * @property {string} [ip] - The ip address for the connector.
 * @property {string} [password] - The password for the connector (required in [PostgreSQL,FTP]).
 * @property {number} [port] - The port for the connector.
 * @property {string} [schema] - The name of the schema of the database (required in [PostgreSQL]).
 * @property {string} [user] - The user for the connector (required in [PostgreSQL,FTP]).
 */

export const CONNECTOR_SUBTYPES = {
  VECTOR: 'VECTOR',
  RASTER: 'RASTER',
  POINTCLOUD: 'POINTCLOUD',
  THEMATIC: 'THEMATIC',
  GENERIC: 'GENERIC',
  properties: {
    VECTOR: {
      name: 'VECTOR',
      value: 'VECTOR',
      humanValue: 'Vector'
    },
    RASTER: {
      name: 'RASTER',
      value: 'RASTER',
      humanValue: 'Raster'
    },
    POINTCLOUD: {
      name: 'POINTCLOUD',
      value: 'POINTCLOUD',
      humanValue: 'PointCloud'
    },
    THEMATIC: {
      name: 'THEMATIC',
      value: 'THEMATIC',
      humanValue: 'Thematic'
    },
    GENERIC: {
      name: 'GENERIC',
      value: 'GENERIC',
      humanValue: 'Generic'
    }
  }
};

export const CONNECTOR_TYPES = {
  FTP: 'FTP',
  GEOJSONVT: 'GeoJSONVirtual',
  HTTP: 'HTTP',
  POSTGRESQL: 'PostgreSQL',
  WMS: 'WMS',
  properties: {
    FTP: {
      name: 'FTP',
      value: 'FTP',
      humanValue: 'FTP'
    },
    GEOJSONVT: {
      name: 'GEOJSONVT',
      value: 'GeoJSONVirtual',
      humanValue: 'GeoJSON VT'
    },
    HTTP: {
      name: 'HTTP',
      value: 'HTTP',
      humanValue: 'HTTP'
    },
    POSTGRESQL: {
      name: 'POSTGRESQL',
      value: 'PostgreSQL',
      humanValue: 'PostgreSQL'
    },
    WMS: {
      name: 'WMS',
      value: 'WMS',
      humanValue: 'WMS'
    }
  }
};

export const FILE_TYPES = {
  CSV: 'CSV',
  ECW: 'ECW',
  EXCEL: 'EXCEL',
  FIWARE: 'FIWARE',
  GEOJSON: 'GeoJSON',
  GEOJSONVT: 'GeoJSONVirtual',
  GEOPACKAGE: 'GeoPackage',
  GEOTIFF: 'GeoTIFF',
  GML: 'GML',
  JP2: 'JP2',
  KML: 'KML',
  LASS: 'LASS',
  MDB: 'MDB',
  RAR: 'rar',
  SENTINEL: 'sentinel',
  SHAPE: 'SHAPE',
  TOPOJSON: 'TopoJSON',
  ZIP: 'zip',
  ZIP7: '7zip',

  properties: {
    CSV: { name: 'CSV', value: 'CSV', humanValue: 'CSV', extension: 'csv' },
    ECW: { name: 'ECW', value: 'ECW', humanValue: 'ECW', extension: 'ecw' },
    EXCEL: { name: 'EXCEL', value: 'EXCEL', humanValue: 'Excel', extension: 'xlsx' },
    FIWARE: { name: 'FIWARE', value: 'FIWARE', humanValue: 'Fiware', extension: '' },
    GEOJSON: { name: 'GEOJSON', value: 'GeoJSON', humanValue: 'GeoJSON', extension: 'geojson' },
    GEOJSONVT: {
      name: 'GEOJSONVT',
      value: 'GeoJSONVirtual',
      humanValue: 'GeoJSON VT',
      extension: ''
    },
    GEOPACKAGE: {
      name: 'GEOPACKAGE',
      value: 'GeoPackage',
      humanValue: 'GeoPackage',
      extension: 'gpkg'
    },
    GEOTIFF: { name: 'GEOTIFF', value: 'GeoTIFF', humanValue: 'GeoTIFF', extension: 'tif' },
    GMl: { name: 'GML', value: 'GML', humanValue: 'GML', extension: 'gml' },
    JP2: { name: 'JP2', value: 'JP2', humanValue: 'JP2', extension: 'jp2' },
    KML: { name: 'KML', value: 'KML', humanValue: 'KML', extension: 'kml' },
    LASS: { name: 'LASS', value: 'LASS', humanValue: 'LASS', extension: 'las' },
    MDB: { name: 'MDB', value: 'MDB', humanValue: 'MDB', extension: 'tif' },
    RAR: { name: 'RAR', value: 'rar', humanValue: 'Rar', extension: 'rar' },
    SENTINEL: { name: 'SENTINEL', value: 'sentinel', humanValue: 'Sentinel', extension: 'tif' },
    SHAPE: { name: 'SHAPE', value: 'SHAPE', humanValue: 'Shape', extension: 'shp' },
    TOPOJSON: { name: 'TOPOJSON', value: 'TopoJSON', humanValue: 'TopoJSON', extension: 'topo' },
    ZIP: { name: 'ZIP', value: 'zip', humanValue: 'Zip', extension: 'zip' },
    ZIP7: { name: 'ZIP7', value: '7zip', humanValue: '7zip', extension: '7zip' }
  }
};

export const FTP_SUBTYPES = pick(CONNECTOR_SUBTYPES, [
  'VECTOR',
  'RASTER',
  'POINTCLOUD',
  'THEMATIC',
  'GENERIC'
]);

export const POSTGRESQL_SUBTYPES = pick(CONNECTOR_SUBTYPES, ['VECTOR', 'THEMATIC']);

export const HTTP_SUBTYPES = pick(CONNECTOR_SUBTYPES, [
  'VECTOR',
  'RASTER',
  'POINTCLOUD',
  'THEMATIC',
  'GENERIC'
]);

export const GEOJSONVT_SUBTYPES = pick(CONNECTOR_SUBTYPES, ['VECTOR']);

/**
 * Get the subtypes allowed for each connector type.
 *
 * @param {string} connectorType - The connector type.
 * @param {string} [resultFormat="array"] - The format for the connector subtypes.
 * @returns {string[]|object} The connector subtypes allowed for each type.
 * If resultFormat is array or anything else, returns an array of the string values.
 * If resultFormat is object, returns an key value object.
 */
export const getSubTypesForType = (connectorType, resultFormat = 'array') => {
  if (resultFormat === 'object') {
    switch (connectorType) {
      case CONNECTOR_TYPES.FTP:
      case CONNECTOR_TYPES.WMS:
        return FTP_SUBTYPES;
      case CONNECTOR_TYPES.POSTGRESQL:
        return POSTGRESQL_SUBTYPES;
      case CONNECTOR_TYPES.HTTP:
        return HTTP_SUBTYPES;
      case CONNECTOR_TYPES.GEOJSONVT:
        return GEOJSONVT_SUBTYPES;
      default:
        return {};
    }
  }
  switch (connectorType) {
    case CONNECTOR_TYPES.FTP:
    case CONNECTOR_TYPES.WMS:
      return values(FTP_SUBTYPES);
    case CONNECTOR_TYPES.POSTGRESQL:
      return values(POSTGRESQL_SUBTYPES);
    case CONNECTOR_TYPES.HTTP:
      return values(HTTP_SUBTYPES);
    case CONNECTOR_TYPES.GEOJSONVT:
      return values(GEOJSONVT_SUBTYPES);
    default:
      return [];
  }
};

const Connectors = new Mongo.Collection('Connectors');

Connectors.allow({
  insert: () => false,
  update: () => false,
  remove: () => false
});

Connectors.deny({
  insert: () => true,
  update: () => true,
  remove: () => true
});

Connectors.schema = new SimpleSchema({
  createdAt: {
    type: String,
    label: 'The date this connection was created in the database',
    optional: true,
    autoValue() {
      if (this.isInsert) return new Date().toISOString();
    }
  },
  deleted: {
    type: String,
    label: 'The date the connection was deleted',
    optional: true,
    autoValue() {
      if (this.isInsert) return 'no';
    }
  },
  isOutput: {
    type: Boolean,
    label: 'Flag to check if the connector is an output one',
    optional: true
  },
  name: {
    type: String,
    label: 'A name to identify the connection',
    optional: true
  },
  owner: {
    type: String,
    label: 'The ID of the user this connection belongs to',
    optional: true
  },
  subtype: {
    type: String,
    label: 'The subtype of the connection added',
    optional: true,
    custom() {
      // inserts
      if (!this.operator && (!this.isSet || this.value === null || this.value === ''))
        return SimpleSchema.ErrorTypes.REQUIRED;
      // updates
      if (this.isSet) {
        if (this.operator === '$set' && (this.value === null || this.value === ''))
          return SimpleSchema.ErrorTypes.REQUIRED;
        if (this.operator === '$unset') return SimpleSchema.ErrorTypes.REQUIRED;
        if (this.operator === '$rename') return SimpleSchema.ErrorTypes.REQUIRED;
      }
      const allowedValues = getSubTypesForType(this.siblingField('type').value);
      if (!includes(allowedValues, this.value)) return SimpleSchema.ErrorTypes.VALUE_NOT_ALLOWED;
    }
  },
  type: {
    type: String,
    label: 'The type of the connection added',
    allowedValues: map(CONNECTOR_TYPES.properties, 'value')
  },
  updatedAt: {
    type: String,
    label: 'The date this connection was last updated',
    optional: true,
    autoValue() {
      if (this.isInsert || this.isUpdate) return new Date().toISOString();
    }
  },
  databaseName: {
    type: String,
    label: 'The name of the database for the connection',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: CONNECTOR_TYPES.POSTGRESQL === this.siblingField('type').value,
        schemaInstance: this
      });
    }
  },
  domain: {
    type: String,
    label: 'The domain for the connection',
    optional: true
  },
  filePath: {
    type: String,
    label: 'The file path for the connection',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [CONNECTOR_TYPES.HTTP, CONNECTOR_TYPES.FTP],
          this.siblingField('type').value
        ),
        schemaInstance: this
      });
    }
  },
  fileType: {
    type: String,
    label: 'The file type for the connection',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [CONNECTOR_TYPES.HTTP, CONNECTOR_TYPES.FTP],
          this.siblingField('type').value
        ),
        schemaInstance: this
      });
    }
  },
  ip: {
    type: String,
    label: 'The ip address for the connection',
    optional: true
  },
  password: {
    type: String,
    label: 'The password for the connection',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [CONNECTOR_TYPES.POSTGRESQL, CONNECTOR_TYPES.FTP],
          this.siblingField('type').value
        ),
        schemaInstance: this
      });
    }
  },
  port: {
    type: SimpleSchema.Integer,
    label: 'The port for the connection',
    optional: true,
    min: 0,
    max: 65535
  },
  schema: {
    type: String,
    label: 'The name of the schema of the database',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: CONNECTOR_TYPES.POSTGRESQL === this.siblingField('type').value,
        schemaInstance: this
      });
    }
  },
  user: {
    type: String,
    label: 'The user for the connection',
    optional: true,
    custom() {
      return requiredIf({
        requiredCondition: includes(
          [CONNECTOR_TYPES.POSTGRESQL, CONNECTOR_TYPES.FTP],
          this.siblingField('type').value
        ),
        schemaInstance: this
      });
    }
  }
});

Connectors.attachSchema(Connectors.schema);

export default Connectors;
